import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router'
import dayjs from 'dayjs'
import { DeleteOutlined, Add as AddIcon, FlashOnOutlined, Note as NoteIcon, TimerOutlined } from '@mui/icons-material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useActionApi } from 'hooks'
import Api from 'api'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import DentistNavbar from 'components/DentistNavbar'
import {
  BasicContainer,
  ButtonContainer,
  Card,
  Title,
  LocationContainer,
  LocationIcon,
  ActionButton,
  RemoveButton,
  AccessTimeIcon,
  EventIcon,
  ButtonIcon,
  BoostCard,
  BoostBadge,
  Subheader,
  BackButton,
  InstabookPendingCard,
  LatePaymentPolicyLink,
} from './JobPostedPage.styles'

import {
  exitReact,
  formatDentistPaymentTermsWithMethod,
  formatProfileHygienistName,
  formatTalentDisplayName,
  getLatePaymentAgreementPrefix,
} from 'utils';
import { formatFirstLicensedLabel } from 'utils/educationCredentials';
import { getCanonicalMembershipBadge } from 'utils/membership';
import { formatPartialOfferTime, debugTimeFormatting } from 'utils/timeFormatting';
import { BOOST_AVAILABILITY_STATUS, BOOST_STATUS } from 'utils/constants';
import { Analytics, is123DentistLocation, trackBoostRestricted123D } from 'utils/analytics';
import { SimpleModal, CorporateRestrictionModal, BrandedAlertModal } from 'components/Modal';
import type { BrandedAction } from 'components/Modal';
import { OfferClockIcon, OfferUnavailableIcon } from 'components/Modal/offerModalIcons';
import DefaultButton from 'components/Buttons/DefaultButton';
import clsx from 'clsx'
import PaymentRequiredModal from '../DentistHomePage/components/PaymentRequiredModal/PaymentRequiredModal'
import { HygienistProps, UserAccountProps, PartialOffer as PartialOfferType } from 'utils/types'
import { PARTIAL_OFFER_STATUS, JOB_STATUSES } from 'utils/constants'
import { dismissOffer, filterDismissedOffers } from 'utils/dismissedOffers'
import { Language } from 'api/Api'
import { Icon } from '../../hygienist/AvailableJobsPage/AvailableJobsPage.styles'
import { getRevenueCustomerId, hasOfficeRevenuePaymentInfo } from 'utils/paymentPurpose'
import { PaymentPromptCheckpoint } from 'utils/paymentPromptAnalytics'
import { toOfficeCancellationDisplayError } from 'utils/officeCancellationError'
import heartFilled from '../../../images/heartFilled.svg'
import heartUnfilled from '../../../images/heartUnfilled.svg'
import ProImage from '../../../components/Icons/ProImage'
import EliteImage from '../../../components/Icons/EliteImage'
import EditProposedRateModal from './EditProposedRateModal'
import { canEditProposedRate } from './proposedRateEdit'
import {
  ClickContainer,
  UserContainerWithBorder,
  RightContainer,
  LeftContainer,
  TopContainer,
  Value,
  ValueBox,
  Subtitle,
  PartialHoursContainer,
  DashedCircle,
  JobSeekingPill
} from '../CustomOffersPage/CustomOffersPage.styles'

interface ShiftTasks {
  [key: string]: any;
  id?: number;
  jobId?: number;
  attire?: number;
}

const LATE_PAYMENT_POLICY_URL = 'https://www.tempstars.com/direct_payment/';

interface JobWithBoostStatus {
  id: number;
  locationId?: number;
  shifts: Array<{
    shiftDate: string;
    postedStart: string;
    postedEnd: string;
  }>;
  location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    country?: number;
    scentFreePolicy?: string | null;
    isCentralBooked?: number;
  };
  type: number;
  status: number;
  proposedRate?: number;
  paymentTerms: number;
  paymentMethod?: number | null;
  hasBonus?: boolean;
  bonus?: number;
  ghr?: number;
  Instabook?: number;
  Autobook?: number;
  hygienistId?: number;
  hygienist?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    graduationYear?: number;
    status?: number;
    skills?: any[];
    [key: string]: any;
  };
  instabookTimeLeft?: number;
  cancelledOffer?: boolean;
  isBoostDisplayed?: boolean;
  shiftTasks?: ShiftTasks;
  shiftNotes?: string | null;
  partialOffers?: PartialOfferType[];
  boostStatus?: {
    isBoosted: boolean;
    status: string;
  };
}

export const getJobLocationPaymentStatus = (
  job: { locationId?: number | string } | null | undefined,
  user: UserAccountProps | null | undefined,
) => {
  try {
    const locationId = job?.locationId
    const loc = user?.locations?.find(l => Number(l.id) === Number(locationId)) as any
    if (!loc) return { requiresCardSetup: false, isCardInvalid: false }
    if (!getRevenueCustomerId(loc)) return { requiresCardSetup: true, isCardInvalid: false }
    if (!hasOfficeRevenuePaymentInfo(loc)) return { requiresCardSetup: false, isCardInvalid: true }
    return { requiresCardSetup: false, isCardInvalid: false }
  } catch {
    return { requiresCardSetup: false, isCardInvalid: false }
  }
}

export const buildJobPostedPaymentInfoRoute = (
  job: { id?: number | string; locationId?: number | string },
  locId?: number | null,
  checkpointOrigin: PaymentPromptCheckpoint = 'offer_details_view',
) => ({
  pathname: 'paymentInfo',
  state: {
    locationId: locId || job.locationId,
    nextPage: 'jobPosted',
    jobId: job.id,
    paymentPurpose: 'revenue',
    sourcePage: 'job_posted',
    checkpointOrigin,
  },
})

export const isPendingInstaBookRequest = (
  job: Pick<JobWithBoostStatus, 'Autobook' | 'hygienistId' | 'cancelledOffer'> | null | undefined,
) => Number(job?.Autobook || 0) === 1
  && Number(job?.hygienistId || 0) > 0
  && !job?.cancelledOffer

export default function JobPostedPage() {
  const history = useHistory()
  const [job, setJob] = useState<JobWithBoostStatus | null>(null)
  const [offers, setOffers] = useState<PartialOfferType[]>([])
  const [availableNow, setAvailableNow] = useState<HygienistProps[]>([])
  const [refreshing, setRefreshing] = useState<boolean>(false)
  const [selectedHygienist, setSelectedHygienist] = useState<HygienistProps | null>(null)
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [confirmMessage, setConfirmMessage] = useState<string>('')
  const [actionMessage, setActionMessage] = useState<string>('')
  const [user, setUser] = useState<UserAccountProps | null>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [requiresCardSetup, setRequiresCardSetup] = useState(false)
  const [isCardInvalid, setIsCardInvalid] = useState(false)
  const [paymentLocationId, setPaymentLocationId] = useState<number | null>(null)
  const [paymentCheckpoint, setPaymentCheckpoint] = useState<PaymentPromptCheckpoint>('offer_details_view')
  const [showConfirmRemove, setShowConfirmRemove] = useState(false)
  const [boostLoading, setBoostLoading] = useState(false)
  const [boostAvailability, setBoostAvailability] = useState<{ status: 'available' | 'queued' | 'unavailable'; activeCount: number; roleType: 0 | 1 } | null>(null)
  const [boostMessage, setBoostMessage] = useState<string>('')
  const [isTurnOffBoostConfirmOpen, setIsTurnOffBoostConfirmOpen] = useState(false)
  const [turnOffBoostLoading, setTurnOffBoostLoading] = useState(false)
  const [resumeData, setResumeData] = useState<any | null>(null)
  const [offerUnavailableMessage, setOfferUnavailableMessage] = useState<string>('')
  // T2-5858: when the DO clicks a REMOVED offer card, we pop a modal that
  // lets them dismiss it AND removes the card from the list on Okay. We track
  // the offer id so we can splice it out of local state when confirmed.
  const [offerUnavailableTitle, setOfferUnavailableTitle] = useState<string>('Offer Unavailable')
  const [offerUnavailableOfferId, setOfferUnavailableOfferId] = useState<number | null>(null)
  const [is123DBoostModalOpen, setIs123DBoostModalOpen] = useState(false)
  // T2-6186: inline "Offer expired" popup with a one-click Request Resubmit
  // action (replaces routing EXPIRED offers to the detail page on this unified
  // posted-shift page, which is the live DO surface when unifiedPostedShiftEnabled).
  const [expiredOffer, setExpiredOffer] = useState<PartialOfferType | null>(null)
  const [resubmitError, setResubmitError] = useState<string>('')
  const [resubmitLoading, setResubmitLoading] = useState(false)
  // T2-5707: edit proposed rate from posted shift details
  const [isEditRateOpen, setIsEditRateOpen] = useState(false)

  // Helper function to get role string
  const getRoleString = (jobType: number) => {
    switch (jobType) {
      case 0:
        return 'Dental Hygienist';
      case 1:
        return 'Dental Assistant';
      case 2:
        return 'Reception/Admin';
      default:
        return 'Dental Professional';
    }
  }

  // Helper function to get role string (lowercase)
  const getRoleStringLower = (jobType: number) => {
    switch (jobType) {
      case 0:
        return 'hygienist';
      case 1:
        return 'assistant';
      case 2:
        return 'admin';
      default:
        return 'professional';
    }
  }

  // Helper function to format task names
  const formatTaskName = (taskKey: string) => {
    return taskKey.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  }

  // Helper function to format attire
  const formatAttire = (attire: number | undefined) => {
    switch (attire) {
      case 0:
        return 'Business Attire';
      case 1:
        return 'Scrubs';
      case 2:
        return 'Either Business or Scrubs';
      default:
        return 'Not specified';
    }
  }

  const onInfoLinkClick = (infoLink: string) => {
    // @ts-ignore
    window.Cordova ? cordova.InAppBrowser.open(
      infoLink,
      '_system',
      'location=yes'
    )
    : window.open(
      infoLink,
      '_blank',
      'location=yes'
    )
  }

  // T2-6356: single, directly-callable refresh used by mount, focus,
  // notification deep-links, live socket updates, and the manual refresh
  // button. Previously this lived inside an effect closure and was only
  // reachable through a fragile jobPosted:refresh -> synthetic
  // visibilitychange -> fetchUnified hop, which silently missed when the
  // page was navigated to from a notification (offers stayed stale until the
  // DO backed out and re-opened the shift).
  const refreshPageData = useCallback(async () => {
    if (!job?.id) return
    try {
      setRefreshing(true)
      await Api.updateToken()
      const [j, avail, boostStatus] = await Promise.all([
        Api.getJobDetails(job.id),
        Api.getAvailableHygienists({ jobId: job.id }),
        Api.getBoostStatus(job.id).catch(() => null),
      ])
      setJob((prev: any) => ({
        ...(j as any),
        boostStatus: boostStatus ?? (prev as any)?.boostStatus ?? { isBoosted: false, status: BOOST_STATUS.NONE },
      }))
      const partials: PartialOfferType[] = (j?.partialOffers || []) as any
      const jobPostedOnTs = (j as any)?.postedOn ? new Date((j as any).postedOn).getTime() : 0
      const isCurrentCycle = (o: any) => {
        if (!jobPostedOnTs) return true
        const created = o?.createdOn ? new Date(o.createdOn).getTime() : 0
        return created >= jobPostedOnTs
      }
      const filtered = (partials || []).filter((o: any) => {
        if (o.isResubmitRequested) return false
        if (o.status === PARTIAL_OFFER_STATUS.POSTED) return true
        if (o.status === PARTIAL_OFFER_STATUS.EXPIRED
          || o.status === PARTIAL_OFFER_STATUS.REMOVED) {
          return isCurrentCycle(o)
        }
        return false
      })
      setOffers(filterDismissedOffers(filtered))
      setAvailableNow((avail?.hygienists || []) as any)
    } catch (e) {
      // noop
    } finally {
      setRefreshing(false)
    }
  }, [job?.id])

  useEffect(() => {
    const loadJob = async () => {
      let id: number | undefined = (history.location.state as any)?.id;
      if (!id) {
        try {
          // Fallback: parse jobId from hash or search (deep-link from email: #/jobPosted?jobId=123)
          const hash = window.location.hash || '';
          const queryPart = hash.includes('?') ? hash.substring(hash.indexOf('?')) : (history.location.search || '');
          if (queryPart) {
            const params = new URLSearchParams(queryPart.replace('#', ''));
            const parsed = Number(params.get('jobId'));
            if (!isNaN(parsed) && parsed > 0) {
              id = parsed;
              // Normalize navigation so subsequent actions have state.id
              try { history.replace('jobPosted', { id }); } catch {}
            }
          }
        } catch {}
      }
      if (id) {
        try {
          const jobData = await Api.getJobDetails(id)
          setJob(jobData)
        } catch (err) {
          console.error('Error loading job:', err)
          history.push('dentistHome')
        }
      }
    }
    loadJob()
  }, [history])

  // Fetch user for payment gating
  useEffect(() => {
    (async () => {
      try {
        const u = await Api.getUser()
        setUser(u as any)
      } catch {}
    })()
  }, [])

  useEffect(() => {
    const loadBoostStatus = async () => {
      if (!job?.id) return;
      try {
        await Api.updateToken();
        const [availability, status] = await Promise.all([
          Api.getBoostAvailability(job.id),
          Api.getBoostStatus(job.id).catch(() => ({ isBoosted: false, status: BOOST_STATUS.NONE })),
        ]);
        setBoostAvailability(availability);
        setJob((prev: JobWithBoostStatus | null) => ({ ...(prev || {}), boostStatus: status } as JobWithBoostStatus));
      } catch (e) {
        setBoostAvailability(null);
        setJob((prev: JobWithBoostStatus | null) => ({ ...(prev || {}), boostStatus: { isBoosted: false, status: BOOST_STATUS.NONE } } as JobWithBoostStatus));
      }
    };
    loadBoostStatus();
  }, [job?.id])

  // Unified page data: offers + available-now. Runs on mount (once job.id is
  // known) and whenever the tab/app regains focus.
  useEffect(() => {
    refreshPageData()

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refreshPageData()
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [refreshPageData])

  // T2-6356: refresh offers when a notification deep-links here or the job
  // updates live over the socket. Both call refreshPageData() directly so the
  // Custom Offers list updates in place without the DO needing to back out and
  // re-open the shift.
  useEffect(() => {
    if (!job?.id) return undefined;
    const socket = (window as any)?.TempStars?.socket;
    // The API emits these for this job when an offer is created / changes
    // (partialOffer.controller). 'newPartialOfferSubmitted' is the one fired on
    // a brand-new offer, so the DO sitting on this page sees the Custom Offer
    // appear live without re-opening the shift. 'jobUpdated' covers other
    // server-side job changes.
    const onJobEvent = (payload: any) => {
      if (Number(payload?.jobId) === Number(job.id)) {
        refreshPageData();
      }
    };
    socket?.on?.('newPartialOfferSubmitted', onJobEvent);
    socket?.on?.('updatedPartialOffer', onJobEvent);
    socket?.on?.('cancelledPartialOffer', onJobEvent);
    socket?.on?.('jobUpdated', onJobEvent);
    const onRefresh = () => {
      refreshPageData();
    };
    document.addEventListener('jobPosted:refresh', onRefresh);
    return () => {
      socket?.off?.('newPartialOfferSubmitted', onJobEvent);
      socket?.off?.('updatedPartialOffer', onJobEvent);
      socket?.off?.('cancelledPartialOffer', onJobEvent);
      socket?.off?.('jobUpdated', onJobEvent);
      document.removeEventListener('jobPosted:refresh', onRefresh);
    };
  }, [job?.id, refreshPageData]);

  // T2-6356: when the DO lands here from a notification (incl. re-tapping while
  // already on this shift), refetch immediately so the new offer is visible.
  useEffect(() => {
    const state = history.location.state as { fromNotification?: boolean; id?: number } | undefined;
    if (state?.fromNotification && state?.id && job?.id && Number(state.id) === Number(job.id)) {
      refreshPageData();
    }
  }, [history.location.state, job?.id, refreshPageData]);

  const handleManualRefresh = async () => {
    await refreshPageData()
  }

  const openPaymentModalIfNeeded = (checkpoint: PaymentPromptCheckpoint): boolean => {
    const status = getJobLocationPaymentStatus(job, user)
    if (status.requiresCardSetup || status.isCardInvalid) {
      setPaymentLocationId(job?.locationId || null)
      setRequiresCardSetup(!!status.requiresCardSetup)
      setIsCardInvalid(!!status.isCardInvalid)
      setPaymentCheckpoint(checkpoint)
      setIsPaymentModalOpen(true)
      return true
    }
    return false
  }

  const handleViewAvailableDetails = async (h: HygienistProps) => {
    setSelectedHygienist(h)
    setDetailsOpen(true)
    // Prefetch resume details for richer preview
    try {
      if (job?.locationId && (h as any)?.id) {
        const res = await Api.getResumeData({
          hygienistId: (h as any).id,
          locationId: job.locationId,
          jobId: job.id,
        })
        setResumeData(res)
      }
    } catch {
      setResumeData(null)
    }
  }

  const handleBookNow = () => {
    if (!job || !selectedHygienist) return
    // Payment gating
    if (openPaymentModalIfNeeded('instabook_book_now')) return
    setConfirmMessage(`By confirming, you will send an Insta-Book request to ${selectedHygienist.firstName || 'this hygienist'}. They will have up to 15 minutes to accept. If accepted, your shift is booked.`)
    setConfirmOpen(true)
  }

  const confirmInstabook = async () => {
    if (!job || !selectedHygienist) return
    try {
      await Api.updateToken()
      await Api.instabookHygienist({ jobId: job.id, hygienistId: (selectedHygienist as any).id })
      setConfirmOpen(false)
      setActionMessage('Insta-Book request sent. We\'ve notified the hygienist. You\'ll receive an email when they accept or decline.')
      // refresh lists
      await handleManualRefresh()
      // if job moved to confirmed, navigate
      try {
        const updated = await Api.getJob(job.id)
        if (Number(updated?.status) === JOB_STATUSES.CONFIRMED || Number(updated?.status) === 3) {
          history.push({ pathname: 'dentistJobConfirmed', state: { id: job.id, jobCount: 1 } })
        }
      } catch {}
    } catch (e) {
      setConfirmOpen(false)
      setActionMessage('Failed to send Insta-Book request. Please try again.')
    }
  }

  const handleGoPaymentInfo = (
    locId: number | null,
    checkpointOrigin: PaymentPromptCheckpoint,
  ) => {
    setIsPaymentModalOpen(false)
    if (!job) return
    history.push(buildJobPostedPaymentInfoRoute(job, locId, checkpointOrigin))
  }

  const goHome = () => {
    history.push('dentistHome')
  }

  // Navbar user info
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.()
  const dentist = currentUser?.dentist
  const firstName = currentUser?.firstName || ''
  const lastName = currentUser?.lastName || ''
  const photoUrl = currentUser?.photoUrl || dentist?.photoUrl || undefined

  const handleGetTemp = () => {
    try {
      ;(window as any).TempStars?.App?.goReact?.('postJob')
    } catch {
      history.push('postJob' as any)
    }
  }

  const handleBackNavigation = () => {
    // Prefer true "back" behaviour; fall back to home when there is no stack.
    try {
      if ((history as any)?.length > 1) {
        history.goBack()
        return
      }
    } catch {}
    goHome()
  }

  const [removeJobLoading, removeJobError, removeJob] = useActionApi({
    apiMethod: Api.removeJob,
    onSuccess: () => {
      try {
        (window as any).TempStars?.App?.goReact('dentistHome');
      } catch {
        history.push('dentistHome');
      }
    },
  })

  if (!job) {
    return null
  }

  if (!job.shifts || !Array.isArray(job.shifts) || !job.shifts.length) {
    console.error('Job shifts data is invalid');
    goHome();
    return;
  }

  const isPendingInstaBook = isPendingInstaBookRequest(job)

  const handleModifyHours = () => {
    if (!job?.shifts?.length) {
      console.error('Job shifts data is invalid');
      return;
    }
    history.push('modifyJob', { job })
  }

  const handleEditProposedRate = () => {
    if (!job || !canEditProposedRate(job.status)) return
    setIsEditRateOpen(true)
  }

  const handleProposedRateSaved = (proposedRate: number) => {
    setJob((prev) => (prev ? { ...prev, proposedRate } : prev))
    setIsEditRateOpen(false)
  }

  const handleRemoveShift = () => {
    setShowConfirmRemove(true)
  }

  const handleConfirmRemove = async () => {
    await removeJob(job.id)
  }

  const handleBookAnother = () => {
    if (!job?.shifts?.length) {
      console.error('Job or shifts are undefined');
      return;
    }
    history.push('postJob', { date: job.shifts[0].shiftDate })
  }

  // Helper function to get role-specific text
  const getRoleSpecificText = (type: number) => {
    switch(type) {
      case 0:
        return {
          professional: "hygienist",
          memberType: "hygienist",
          memberCount: "8000 hygienist"
        };
      case 1:
        return {
          professional: "dental assistant",
          memberType: "assistant",
          memberCount: "8000 assistant"
        };
      case 2:
        return {
          professional: "TempStars dental professional who qualifies for booking your Admin/Reception shift",
          memberType: "Admin/Receptionist member",
          memberCount: "1000's of TempStars members who qualify for your shift needs,"
        };
      default:
        return {
          professional: "dental professional",
          memberType: "member",
          memberCount: "8000"
        };
    }
  };

  const roleText = getRoleSpecificText(job?.type || 0);

  // Distance label based on location country (0 = Canada, 1 = US)
  const distanceLabel = (() => {
    try {
      const loc = user?.locations?.find(l => Number(l.id) === Number(job?.locationId));
      if (typeof loc?.country === 'number') {
        if (loc.country === 1) return '50mi'; // US
        if (loc.country === 0) return '110km'; // Canada
      }
      return '110km';
    } catch {
      return '110km';
    }
  })();

  // Role labels for prose
  const roleLower = getRoleStringLower(job?.type || 0);
  const rolePlural = (() => {
    if (roleLower === 'assistant') return 'assistants';
    if (roleLower === 'hygienist') return 'hygienists';
    if (roleLower === 'admin') return 'admins';
    return `${roleLower}s`;
  })();

  // Role display per spec for templated messaging
  const roleDisplay = (() => {
    switch (job?.type) {
      case 0: return 'Dental Hygienist';
      case 1: return 'Dental Assistant';
      case 2: return 'All qualified candidates';
      default: return 'All qualified candidates';
    }
  })();
  const notifiedPhrase = job?.type === 2 ? 'All qualified candidates' : `every ${roleDisplay}`;
  const notifiedVerb = (notifiedPhrase || '').startsWith('All ') ? 'have' : 'has';
  const rolePluralDisplay = (() => {
    switch (job?.type) {
      case 0: return 'Dental Hygienists';
      case 1: return 'Dental Assistants';
      case 2: return 'qualified candidates';
      default: return 'qualified candidates';
    }
  })();

  const handleBoost = async () => {
    if (!job?.id) return;
    try {
      setBoostLoading(true);
      await Api.updateToken();
      const result = await Api.boostShift(job.id);
      // Boost was successful
      const successMessage = result?.status === BOOST_STATUS.IN_ROTATION
        ? "Shift boosted! You'll be charged $28.25 on first offer."
        : "Added to boost queue. We'll notify you when activated.";
      setBoostMessage(successMessage);
      try {
        const [availability, status] = await Promise.all([
          Api.getBoostAvailability(job.id).catch(() => null),
          Api.getBoostStatus(job.id).catch(() => ({ isBoosted: true, status: result?.status || BOOST_STATUS.IN_ROTATION })),
        ]);
        if (availability) setBoostAvailability(availability);
        setJob((prev: JobWithBoostStatus | null) => ({ ...(prev || {}), boostStatus: status } as JobWithBoostStatus));
      } catch {}
    } catch (e: any) {
      console.error('Boost error:', e);
      // Try to extract a meaningful error message
      let errorMsg = 'Failed to boost shift. Please try again.';
      try {
        if (e?.message) {
          const parsed = typeof e.message === 'string' && e.message.startsWith('{') ? JSON.parse(e.message) : null;
          if (parsed?.message) {
            errorMsg = parsed.message;
          } else if (typeof e.message === 'string') {
            errorMsg = e.message;
          }
        }
      } catch {}
      setBoostMessage(errorMsg);
    } finally {
      setBoostLoading(false);
    }
  }

  const handleTurnOffBoost = async () => {
    if (!job?.id || turnOffBoostLoading) return;
    try {
      setTurnOffBoostLoading(true);
      await Api.updateToken();
      await Api.turnOffBoost(job.id);
      Analytics.track('Boost Turned Off', {
        job_id: job.id,
        previous_status: job?.boostStatus?.status || null,
        location_id: job.locationId,
      });
      setIsTurnOffBoostConfirmOpen(false);
      const [availability, status] = await Promise.all([
        Api.getBoostAvailability(job.id).catch(() => null),
        Api.getBoostStatus(job.id).catch(() => ({ isBoosted: true, status: BOOST_STATUS.TURNED_OFF })),
      ]);
      if (availability) setBoostAvailability(availability);
      setJob((prev: JobWithBoostStatus | null) => ({
        ...(prev || {}),
        boostStatus: status,
        isBoostDisplayed: false,
      } as JobWithBoostStatus));
      setBoostMessage('Boosted Post is off for this shift.');
    } catch (e: any) {
      console.error('Turn off boost error:', e);
      let errorMsg = 'Failed to turn off Boosted Post. Please try again.';
      try {
        if (e?.message) {
          const parsed = typeof e.message === 'string' && e.message.startsWith('{') ? JSON.parse(e.message) : null;
          if (parsed?.error) {
            errorMsg = parsed.error;
          } else if (parsed?.message) {
            errorMsg = parsed.message;
          } else if (typeof e.message === 'string') {
            errorMsg = e.message;
          }
        }
      } catch {}
      setIsTurnOffBoostConfirmOpen(false);
      setBoostMessage(errorMsg);
    } finally {
      setTurnOffBoostLoading(false);
    }
  }

  const isBoostActive = !!job?.isBoostDisplayed;

  const scentPolicyLabel = (policy?: string | null) => {
    switch (policy) {
      case 'scent-free':
        return 'Scent-free';
      case 'not-scent-free':
        return 'Not scent-free';
      default:
        return 'Not provided';
    }
  };

  return (
    <>
      <DentistNavbar
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        title="Posted Shift"
        onGetTempClick={handleGetTemp}
        showGetTempButton={true}
      />

      <BasicContainer>
        <Subheader>
          <BackButton onClick={handleBackNavigation} className="no-fastclick" aria-label="Go back">
            <ArrowBackIcon />
          </BackButton>
        </Subheader>
        {/* Auto-refresh on mount and on page visibility; manual Refresh button removed */}
        <Card>
          <Title>
            {getRoleString(job.type)} Shift
          </Title>

          <LocationContainer>
            <LocationIcon />
            <div>{`${job.location.address}, ${job.location.city}, ${job.location.province} ${job.location.postalCode}`}</div>
          </LocationContainer>
          <div style={{ marginTop: 8, fontSize: '0.95rem' }}>
            <strong>Scent-Free Policy:</strong> {scentPolicyLabel(job.location?.scentFreePolicy)}
          </div>

          {/* Date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <EventIcon />
            <span style={{ fontWeight: 600 }}>Date</span>
            <span>{dayjs(job.shifts[0].shiftDate).format('dddd, MMM D')}</span>
          </div>

          {/* Hours + Change link */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            <AccessTimeIcon />
            <span style={{ fontWeight: 600 }}>Start:</span>
            <span>{job.shifts[0].postedStart}</span>
            <span style={{ margin: '0 8px', color: 'var(--text-secondary, #666)' }}>|</span>
            <span style={{ fontWeight: 600 }}>End:</span>
            <span>{job.shifts[0].postedEnd}</span>
            <a
              onClick={handleModifyHours}
              className={clsx('no-fastclick')}
              style={{ marginLeft: 8, cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary-blue, #0B4598)' }}
            >
              Change hours
            </a>
          </div>

          {/* Proposed Rate */}
          <div
            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 12 }}
          >
            <span style={{ fontWeight: 600 }}>Proposed Rate:</span>
            <span data-testid="job-posted-proposed-rate-value">
              {job?.proposedRate ? `$${job.proposedRate}/hr` : '—'}
            </span>
            {canEditProposedRate(job.status) && (
              <a
                id="job-posted-edit-proposed-rate-button"
                data-testid="job-posted-edit-proposed-rate-button"
                aria-label="Edit proposed rate"
                content-desc="job-posted-edit-proposed-rate-button"
                onClick={handleEditProposedRate}
                className={clsx('no-fastclick')}
                style={{ marginLeft: 8, cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary-blue, #0B4598)' }}
              >
                Edit rate
              </a>
            )}
          </div>

          {/* Bonus, conditional */}
          {job.hasBonus && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontWeight: 600 }}>Bonus:</span>{' '}
              <span>{`+$${job.bonus}/hr incentive bonus`}</span>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--divider, #e0e0e0)', margin: '12px 0' }} />

          {/* Payment Terms */}
          <div style={{ marginTop: 4 }}>
            <span style={{ fontWeight: 600 }}>Payment terms:</span>{' '}
            <span className="payment-terms">
              {formatDentistPaymentTermsWithMethod(
                job.paymentTerms,
                job.paymentMethod,
                job.location?.country,
              )}
            </span>
          </div>
          <div style={{ marginTop: 6 }}>
            <span>{getLatePaymentAgreementPrefix(job.type, job.paymentTerms)}</span>
            <LatePaymentPolicyLink
              id="job-posted-late-payment-policy-link"
              data-testid="job-posted-late-payment-policy-link"
              aria-label="Open TempStars Late Payment Policy"
              content-desc="job-posted-late-payment-policy-link"
              className="no-fastclick"
              href={LATE_PAYMENT_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                onInfoLinkClick(LATE_PAYMENT_POLICY_URL);
              }}
            >
              TempStars Late Payment Policy
            </LatePaymentPolicyLink>
          </div>

          {isBoostActive && (
            <div style={{ marginTop: 12 }}>
              <BoostBadge>⚡ Featured shift is active</BoostBadge>
            </div>
          )}

          {job.shiftNotes && job.shiftNotes.trim() && (
            <div style={{
              background: 'rgba(11, 69, 152, 0.04)',
              border: '1px solid rgba(11, 69, 152, 0.15)',
              borderLeft: '4px solid var(--primary-blue, #0B4598)',
              borderRadius: 12,
              padding: 20,
              marginTop: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <NoteIcon style={{ width: 20, height: 20, color: 'var(--primary-blue, #0B4598)' }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Shift Notes</span>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                {job.shiftNotes}
              </div>
            </div>
          )}
        </Card>

        {/* Admin-specific details */}
        {job.type === 2 && job.shiftTasks && (
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Task Requirements</div>
            {Object.entries(job.shiftTasks)
              .filter(([key, value]) => key !== 'id' && key !== 'jobId' && key !== 'attire' && value === 1)
              .map(([key]) => (
                <div key={key} style={{ paddingLeft: '4px', marginBottom: '8px' }}>
                  • {formatTaskName(key)}
                </div>
              ))}

            <div style={{ fontWeight: 600, marginTop: 12 }}>Attire Requirements</div>
            <div>{formatAttire(job.shiftTasks.attire)}</div>
          </Card>
        )}

        {(job?.location?.province === 'ON' || job?.location?.province === 'Ontario') && boostAvailability && boostAvailability.status !== BOOST_AVAILABILITY_STATUS.UNAVAILABLE && (
          <BoostCard>
            {(() => {
              const roleLabel = job.type === 1 ? 'Dental Assistant' : 'Dental Hygienist';
              const status = job?.boostStatus?.status; // none | in_rotation | queued | charged | filled | expired | turned_off
              const turnOffButton = (
                <RemoveButton
                  id="job-posted-turn-off-boost-button"
                  data-testid="job-posted-turn-off-boost-button"
                  aria-label="Turn off Boosted Post"
                  content-desc="job-posted-turn-off-boost-button"
                  variant="outlined"
                  onClick={() => setIsTurnOffBoostConfirmOpen(true)}
                  disabled={turnOffBoostLoading || boostLoading}
                  className={clsx('outlined', 'no-fastclick')}
                >
                  Turn off Boosted Post
                </RemoveButton>
              );
              if (!job?.boostStatus?.isBoosted || status === BOOST_STATUS.NONE) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boost This Shift</div>
                    <div className="boost-desc">Get more visibility and fill your shift faster. $25 + HST ($28.25). You will only be charged when you receive your first offer.</div>
                    <div>
                      {boostAvailability.status === BOOST_AVAILABILITY_STATUS.AVAILABLE ? (
                        <span>✓ Boost slot available</span>
                      ) : (
                        <span>📋 Boost queue active ({boostAvailability.activeCount}/10 in rotation)</span>
                      )}
                    </div>
                    <ActionButton
                      variant="contained"
                      onClick={() => {
                        if (is123DentistLocation(job.location)) {
                          const loc = user?.locations?.find(l => Number(l.id) === Number(job?.locationId)) as any;
                          trackBoostRestricted123D({
                            id: job.locationId,
                            isCentralBooked: job.location?.isCentralBooked,
                            practiceName: loc?.practiceName || '',
                          }, 'job_posted');
                          setIs123DBoostModalOpen(true);
                          return;
                        }
                        handleBoost();
                      }}
                      disabled={boostLoading}
                      className={clsx('contained', 'no-fastclick')}
                    >
                      Boost this shift
                    </ActionButton>
                  </>
                );
              }
              if (status === BOOST_STATUS.QUEUED) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boost Waitlist</div>
                    <div className="boost-desc">
                      Your shift is on the waitlist to be boosted — don't worry, boosted shifts are constantly being booked, so your shift is moving up the waitlist! We'll notify you by email as soon as your shift becomes actively boosted.
                    </div>
                    {turnOffButton}
                  </>
                );
              }
              if (status === BOOST_STATUS.IN_ROTATION) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boosted</div>
                    <div className="boost-desc">
                      This shift is currently Boosted for {roleLabel}. You have not been charged yet; payment only occurs when you receive your first offer while in rotation.
                    </div>
                    {turnOffButton}
                  </>
                );
              }
              if (status === BOOST_STATUS.CHARGED) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boosted</div>
                    <div className="boost-desc">
                      This shift was Boosted and has received at least one offer since boosting. Boosting charges are only processed after receiving an offer.
                    </div>
                    {turnOffButton}
                  </>
                );
              }
              if (status === BOOST_STATUS.FILLED) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boosted</div>
                    <div className="boost-desc">
                      This shift was Boosted and has received at least one offer since boosting. Boosting charges are only processed after receiving an offer.
                    </div>
                  </>
                );
              }
              if (status === BOOST_STATUS.TURNED_OFF) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boosted Post is off</div>
                    <div className="boost-desc">
                      Boosted Post was turned off for this shift. It is no longer in rotation or on the waitlist. Turning it off does not refund a charge that was already triggered while Boosted Post was active. Boost cannot be re-enabled for this shift.
                    </div>
                  </>
                );
              }
              if (status === BOOST_STATUS.EXPIRED) {
                return (
                  <>
                    <div className="boost-title"><FlashOnOutlined /> Boost Ended</div>
                    <div className="boost-desc">This boosted shift has ended (expired/removed). If you repost, you can choose to Boost again.</div>
                  </>
                );
              }
              return null;
            })()}
          </BoostCard>
        )}

        {/* Insta-Book Pending */}
        {isPendingInstaBook && (
          <InstabookPendingCard>
            <div className="instabook-pending-title">
              <FlashOnOutlined style={{ color: 'var(--primary-green, #13CA82)' }} />
              Insta-Book Request Pending
            </div>
            <div style={{ marginTop: 8 }}>
              You have submitted an Insta-Book request to{' '}
              <span className="instabook-pending-name">
                {job?.hygienist?.firstName || 'the hygienist'}
              </span>.
            </div>
            {typeof job?.instabookTimeLeft === 'number' && job.instabookTimeLeft > 0 && (
              <span className="instabook-pending-timer">
                <TimerOutlined style={{ width: 16, height: 16 }} />
                ~{job.instabookTimeLeft} min remaining
              </span>
            )}
            <div className="instabook-pending-body">
              They will confirm or possibly decline within 15 minutes. If you must cancel this shift,
              use Remove Shift below. We will cancel the pending request and notify the talent.
              <br /><br />
              Watch your email over the next 20 minutes for a notification about this request.
            </div>
          </InstabookPendingCard>
        )}

        {/* Available to Book Now */}
        {(availableNow && availableNow.length > 0) && (
          <Card>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div style={{
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                minWidth: 0,
                flex: '1 1 200px',
              }}>
                <span>Available to Book Now</span>
                <span style={{
                  fontSize: 11,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: 'var(--success-main, #13CA82)',
                  color: 'var(--common-white-main, #fff)',
                  flexShrink: 0,
                }}>Instant booking</span>
              </div>
              <div style={{
                color: 'var(--text-secondary, #666)',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}>{availableNow.length} available</div>
            </div>
            <div style={{ marginTop: 8 }}>
              {availableNow.map((h: any, index: number) => {
                const professionalLicense = formatProfileHygienistName(h, h?.skills);
                const membershipBadge = getCanonicalMembershipBadge(
                  h?.membershipStatusCode ?? h?.membershipSummary?.officeStatusLabel,
                );
                const hourlyRate = job?.ghr
                  ? `$${job?.proposedRate || ''}/hr`
                  : `$${h?.hourlyRate || job?.proposedRate || ''}/hr`;
                return (
                  <ClickContainer key={h.id || index} onClick={() => {}}>
                    <UserContainerWithBorder id={(index + (h?.lastName || '')) as any} style={{opacity: 1}}>
                      <TopContainer>
                        <LeftContainer>
                          <Value style={{display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap'}}>
                            {professionalLicense || `${h.firstName || ''} ${h.lastName || ''}`.trim() || 'Professional'}
                            <Icon style={{height: 22, alignSelf: 'center'}} src={h?.isFavouriteHygienist ? heartFilled : heartUnfilled}/>
                            {membershipBadge === 'pro' && <ProImage alt="Pro membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                            {membershipBadge === 'elite' && <EliteImage alt="Elite membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                          </Value>
                          {h.graduationYear && (
                            <Subtitle>Graduated: {h.graduationYear}</Subtitle>
                          )}
                          {formatFirstLicensedLabel(h.firstLicensedYear, h.firstLicensedMonth) && (
                            <Subtitle>{formatFirstLicensedLabel(h.firstLicensedYear, h.firstLicensedMonth)}</Subtitle>
                          )}
                          <div>
                            <a
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!job) return; history.push('customOfferDetails', { mode: 'instabook', job, hygienistId: (h as any).id, instabookHourlyRate: (h as any).hourlyRate }); }}
                              className={clsx('no-fastclick')}
                              style={{ color: 'var(--secondary-main, #0B4598)', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              View Details
                            </a>
                          </div>
                        </LeftContainer>

                        <RightContainer>
                          <Value>{hourlyRate}</Value><i className="icon icon-forward"/>
                        </RightContainer>
                      </TopContainer>
                    </UserContainerWithBorder>
                  </ClickContainer>
                );
              })}
            </div>
          </Card>
        )}

        {/* Custom Offers — hidden entirely while an Insta-Book request is active */}
        {!(job?.Autobook === 1 && !!job?.hygienistId && !job?.cancelledOffer) && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 600 }}>Custom Offers</div>
            {offers?.length > 0 && <div style={{ color: 'var(--text-secondary, #666)' }}>{offers.length} offer{offers.length !== 1 ? 's' : ''}</div>}
          </div>
          <div style={{ marginTop: 8 }}>
            {offers?.length === 0 && (
              <div
                style={{
                  background: (availableNow?.length || 0) > 0
                    ? 'var(--primary-blue-50, #E8F0FE)'
                    : 'var(--background-main, #F7F7F7)',
                  padding: 12,
                  borderRadius: 8,
                  color: 'var(--text-secondary, #666)'
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text-primary, #333)' }}>Get more visibility for your shift</div>
                <div style={{ marginTop: 6, lineHeight: 1.5 }}>
                  No {(job?.type === 2 ? 'qualified candidates' : rolePlural)} have submitted an offer{(availableNow?.length || 0) > 0 ? '' : ' or are immediately available'} for this shift yet. {notifiedPhrase} within {distanceLabel} of your office {notifiedVerb} been alerted — keep an eye on your email for incoming offers. In the meantime, here are some ways to improve your chances:
                </div>
                <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                  <li style={{ marginBottom: 4 }}>
                    <span style={{ color: 'var(--success-main, #13CA82)' }}>• </span>
                    <span><strong>Boost your shift</strong> to increase visibility and get more offers</span>
                  </li>
                  <li>
                    <span style={{ color: 'var(--success-main, #13CA82)' }}>• </span>
                    <span><strong>Adjust your rate</strong> to be more competitive with market rates</span>
                  </li>
                </ul>
              </div>
            )}
            {offers?.map((offer: any, idx: number) => {
              const hygienist = offer.hygienist;
              const professionalLicense = hygienist ? formatProfileHygienistName(hygienist, hygienist?.skills) : '';
              const membershipBadge = getCanonicalMembershipBadge(
                hygienist?.membershipStatusCode ?? hygienist?.membershipSummary?.officeStatusLabel,
              );

              const hourlyRate = job?.ghr
                ? `$${job?.proposedRate || offer?.hourlyRate || ''}/hr`
                : `$${offer?.hourlyRate || job?.proposedRate || ''}/hr`;

              const expiredOffer = offer.status === PARTIAL_OFFER_STATUS.REMOVED
                || offer.status === PARTIAL_OFFER_STATUS.EXPIRED
                || job?.status === JOB_STATUSES.OFFER_EXPIRED;

              // T2-5833: visible pill so the DO can tell at-a-glance whether
              // an offer timed out (can be resubmitted) vs. became unavailable
              // (talent withdrew or got booked elsewhere).
              const offerStateLabel = offer.status === PARTIAL_OFFER_STATUS.REMOVED
                ? 'No longer available'
                : (offer.status === PARTIAL_OFFER_STATUS.EXPIRED
                  ? 'Offer expired'
                  : null);

              const isPartialHours = offer.offeredStartTime !== job?.shifts[0].postedStart || 
                                     offer.offeredEndTime !== job?.shifts[0].postedEnd;

              if ((process.env.NODE_ENV as string) === 'staging' || (process.env.NODE_ENV as string) === 'local') {
                debugTimeFormatting(
                  offer.offeredStartTime,
                  offer.offeredStartTimeLocal,
                  'JobPostedPage - Offer Times'
                );
              }

              const formattedStartTime = formatPartialOfferTime(
                offer.offeredStartTime,
                offer.offeredStartTimeLocal,
                offer.location?.lat,
                offer.location?.lon
              );
              const formattedEndTime = formatPartialOfferTime(
                offer.offeredEndTime,
                offer.offeredEndTimeLocal,
                offer.location?.lat,
                offer.location?.lon
              );

              if (offer?.isResubmitRequested) {
                return null;
              }

              return (
                <ClickContainer key={offer.id || idx} onClick={() => {
                  if (!job) return;
                  // T2-5833:
                  //  - REMOVED offers (talent withdrew or got booked elsewhere)
                  //    show the explanation popup and dismiss with OK.
                  //  - Time-EXPIRED offers route to the offer detail page,
                  //    where the existing "Request this offer to be
                  //    resubmitted" flow lives. We bypass the payment-info
                  //    gate because the DO can't accept this offer anyway.
                  //  - POSTED offers go through the normal payment-info
                  //    gate and into the detail page.
                  if (offer.status === PARTIAL_OFFER_STATUS.REMOVED) {
                    // T2-5858: the backend's getJobDetails now exposes
                    // `wasBookedElsewhere` on REMOVED offers (true when the
                    // talent is CONFIRMED on another shift for the same
                    // date, false when the talent actively withdrew). Show
                    // the copy that matches each case instead of a single
                    // generic "withdrew" string.
                    const roleLabel = job?.type === 1 ? 'assistant' : 'hygienist';
                    if ((offer as any).wasBookedElsewhere) {
                      setOfferUnavailableTitle('This Offer Is No Longer Available');
                      setOfferUnavailableMessage(
                        `This ${roleLabel} just got booked by another office for this day, sorry! Keep an eye out for more offers coming soon!`
                      );
                    } else {
                      setOfferUnavailableTitle('This Offer Was Withdrawn');
                      setOfferUnavailableMessage(
                        `This ${roleLabel} removed their offer and it is no longer available for this day. Watch for more coming soon!`
                      );
                    }
                    setOfferUnavailableOfferId(offer.id as number);
                    return;
                  }
                  if (offer.status === PARTIAL_OFFER_STATUS.EXPIRED) {
                    // T2-6186: open an inline popup with "Okay" + "Request
                    // Resubmit" instead of routing to the detail page.
                    setResubmitError('')
                    setExpiredOffer(offer)
                    return;
                  }
                  if (job?.status === JOB_STATUSES.OFFER_EXPIRED) {
                    setOfferUnavailableMessage('This offer has expired and is no longer available.');
                    return;
                  }
                  history.push('customOfferDetails', {
                    offerId: offer.id,
                    job,
                    partialOffer: offer,
                    offerDetailsSourcePage: 'job_posted',
                  })
                }}>
                  <UserContainerWithBorder id={(idx + (hygienist?.lastName || '')) as any} style={{opacity: expiredOffer ? '0.6' : '1'}}>
                    <TopContainer>
                      <LeftContainer>
                        <Value style={{display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap'}}>
                          {professionalLicense}
                          <Icon style={{height: 22, alignSelf: 'center'}} src={offer?.isFavouriteHygienist ? heartFilled : heartUnfilled}/>
                          {membershipBadge === 'pro' && <ProImage alt="Pro membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                          {membershipBadge === 'elite' && <EliteImage alt="Elite membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                        </Value>

                        <Subtitle>
                          Graduated: {hygienist?.graduationYear}
                        </Subtitle>
                        {formatFirstLicensedLabel(hygienist?.firstLicensedYear, hygienist?.firstLicensedMonth) && (
                          <Subtitle>
                            {formatFirstLicensedLabel(hygienist?.firstLicensedYear, hygienist?.firstLicensedMonth)}
                          </Subtitle>
                        )}

                        {offerStateLabel && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginTop: 4,
                              padding: '2px 8px',
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#6B7280',
                              background: '#F3F4F6',
                              border: '1px solid #E5E7EB',
                              borderRadius: 12,
                            }}
                          >
                            {offerStateLabel}
                          </span>
                        )}

                        {isPartialHours && (
                          <PartialHoursContainer>
                            <DashedCircle />
                            <div style={{color: '#666', marginTop: '4px'}}>
                              Partial Hours Offered: {formattedStartTime} - {formattedEndTime}
                            </div>
                          </PartialHoursContainer>
                        )}

                        {/* TODO (Remove Feature Flag): Remove 'offer.jobSeekingFeatureActive &&' check when 'jobSeekingStatus' flag is retired */}
                        {offer.jobSeekingFeatureActive &&
                         offer.showJobSeekingStatus === 1 &&
                         hygienist &&
                         hygienist.showForHire === 1 && (
                          <div style={{ marginTop: '4px' }}>
                            <JobSeekingPill>
                              Actively Seeking Permanent Employment
                            </JobSeekingPill>
                          </div>
                        )}
                      </LeftContainer>

                      <RightContainer>
                        <Value>{hourlyRate}</Value>View Details<i className="icon icon-forward"/>
                      </RightContainer>
                    </TopContainer>
                  </UserContainerWithBorder>
                </ClickContainer>
              )
            })}
          </div>
        </Card>)
        }

        <ButtonContainer>
          <RemoveButton
            variant="outlined"
            onClick={showConfirmRemove ? handleConfirmRemove : handleRemoveShift}
            color="error"
            className={clsx("outlined", "no-fastclick")}
          >
            <ButtonIcon className="error">
              <DeleteOutlined />
            </ButtonIcon>
            {showConfirmRemove ? 'Tap/click again to confirm' : 'Remove Shift'}
          </RemoveButton>

          <ActionButton
            variant="contained"
            onClick={handleBookAnother}
            className={clsx("contained", "no-fastclick")}
          >
            <ButtonIcon><AddIcon /></ButtonIcon>
            Book another temp for this date
          </ActionButton>
        </ButtonContainer>

        <FixedLoader
          loading={removeJobLoading}
        />
        <FixedError error={toOfficeCancellationDisplayError(removeJobError)}/>
        {/* Action/Simple Modals */}
        <SimpleModal isOpen={!!boostMessage} onClose={() => setBoostMessage('')}>
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>{boostMessage}</div>
            <DefaultButton variant="contained" color="primary" onClick={() => setBoostMessage('')}>OK</DefaultButton>
          </div>
        </SimpleModal>

        {/* T2-6632: confirm turn off Boosted Post */}
        <SimpleModal
          isOpen={isTurnOffBoostConfirmOpen}
          onClose={() => !turnOffBoostLoading && setIsTurnOffBoostConfirmOpen(false)}
        >
          <div style={{ padding: 16 }}>
            <div className="boost-title" style={{ fontWeight: 600, marginBottom: 8 }}>Turn off Boosted Post?</div>
            <div style={{ marginBottom: 16, color: 'var(--text-secondary, #666)' }}>
              This shift will immediately leave Boosted Post rotation or the waitlist.
              Turning it off does not refund or cancel a charge that was already triggered while Boosted Post was active.
              If no charge was triggered yet, you will not be charged later for this activation.
              Boost cannot be turned back on for this shift.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ActionButton
                id="job-posted-keep-boosted-button"
                data-testid="job-posted-keep-boosted-button"
                aria-label="Keep Boosted"
                content-desc="job-posted-keep-boosted-button"
                variant="outlined"
                disabled={turnOffBoostLoading}
                className={clsx('outlined', 'no-fastclick')}
                onClick={() => setIsTurnOffBoostConfirmOpen(false)}
              >
                Keep Boosted
              </ActionButton>
              <RemoveButton
                id="job-posted-confirm-turn-off-boost-button"
                data-testid="job-posted-confirm-turn-off-boost-button"
                aria-label="Turn off Boosted Post"
                content-desc="job-posted-confirm-turn-off-boost-button"
                variant="outlined"
                disabled={turnOffBoostLoading}
                className={clsx('outlined', 'no-fastclick')}
                onClick={handleTurnOffBoost}
              >
                {turnOffBoostLoading ? 'Turning off…' : 'Turn off Boosted Post'}
              </RemoveButton>
            </div>
          </div>
        </SimpleModal>

        {/* T2-5707: Edit proposed rate */}
        <SimpleModal isOpen={isEditRateOpen} onClose={() => setIsEditRateOpen(false)}>
          {job && (
            <EditProposedRateModal
              job={job}
              onClose={() => setIsEditRateOpen(false)}
              onSaved={handleProposedRateSaved}
            />
          )}
        </SimpleModal>

        {/* Available Details Modal */}
        <SimpleModal isOpen={detailsOpen} onClose={() => setDetailsOpen(false)}>
          <div style={{ padding: 16, minWidth: 280 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Professional Details</div>
            <div>{selectedHygienist ? formatTalentDisplayName(selectedHygienist.firstName, selectedHygienist.lastName, selectedHygienist.preferredName) : ''}</div>
            {selectedHygienist?.graduationYear && (
              <div style={{ color: 'var(--text-secondary, #666)', marginTop: 4 }}>Graduated: {selectedHygienist.graduationYear}</div>
            )}
            {formatFirstLicensedLabel(selectedHygienist?.firstLicensedYear, selectedHygienist?.firstLicensedMonth) && (
              <div style={{ color: 'var(--text-secondary, #666)', marginTop: 4 }}>
                {formatFirstLicensedLabel(selectedHygienist?.firstLicensedYear, selectedHygienist?.firstLicensedMonth)}
              </div>
            )}
            {resumeData?.languages && Array.isArray(resumeData.languages) && resumeData.languages.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: 'var(--text-secondary, #666)', marginBottom: 4 }}>Languages</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {resumeData.languages.filter((l: any) => l.code !== 'en').map((l: any) => (
                    <span key={l.id} style={{
                      background: '#ECEFF1', color: '#455A64', fontSize: 10, fontWeight: 600, padding: '2px 4px', borderRadius: 4
                    }}>{(l.code || '').toUpperCase()}</span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <DefaultButton variant="outlined" onClick={() => setDetailsOpen(false)}>Close</DefaultButton>
              <DefaultButton variant="contained" color="primary" onClick={handleBookNow}>Book Now</DefaultButton>
            </div>
          </div>
        </SimpleModal>

        {/* Confirm Instabook Modal */}
        <SimpleModal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>{confirmMessage || 'Confirm Insta-Book?'}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <DefaultButton variant="outlined" onClick={() => setConfirmOpen(false)}>Cancel</DefaultButton>
              <DefaultButton variant="contained" color="primary" onClick={confirmInstabook}>Confirm</DefaultButton>
            </div>
          </div>
        </SimpleModal>

        {/* Generic Acknowledgement */}
        <SimpleModal isOpen={!!actionMessage} onClose={() => setActionMessage('')}>
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>{actionMessage}</div>
            <DefaultButton variant="contained" color="primary" onClick={() => setActionMessage('')}>OK</DefaultButton>
          </div>
        </SimpleModal>

        {/* Offer Unavailable Modal (branded — T2-6186) */}
        <BrandedAlertModal
          isOpen={!!offerUnavailableMessage}
          onClose={() => { setOfferUnavailableMessage(''); setOfferUnavailableOfferId(null); }}
          headerLabel="Custom Offer"
          headerIcon={<OfferUnavailableIcon />}
          illustration={<OfferUnavailableIcon />}
          illustrationVariant="danger"
          title={offerUnavailableTitle}
          ariaLabel={offerUnavailableTitle}
          actions={[{
            label: 'Okay',
            variant: 'primary',
            onClick: () => {
              // T2-5858: on Okay, clear the modal AND splice the REMOVED
              // offer out of the local list so the card disappears for the DO.
              if (offerUnavailableOfferId != null) {
                dismissOffer(offerUnavailableOfferId);
                setOffers((prev) => prev.filter((o: any) => o.id !== offerUnavailableOfferId));
              }
              setOfferUnavailableMessage('');
              setOfferUnavailableTitle('Offer Unavailable');
              setOfferUnavailableOfferId(null);
            },
          }]}
        >
          {offerUnavailableMessage}
        </BrandedAlertModal>

        {/* T2-6186: Offer Expired popup with Request Resubmit (branded modal) */}
        {(() => {
          if (!expiredOffer) return null;
          const fname = expiredOffer?.hygienist?.firstName || 'the talent';
          const alreadyRequested = !!(expiredOffer as any).isResubmitRequested;
          const closeExpired = () => { setExpiredOffer(null); setResubmitError(''); };
          const actions: BrandedAction[] = alreadyRequested
            ? [{ label: 'Okay', variant: 'primary', onClick: closeExpired }]
            : [
                {
                  label: 'Okay',
                  variant: 'secondary',
                  disabled: resubmitLoading,
                  onClick: () => {
                    if (expiredOffer?.id != null) {
                      dismissOffer(expiredOffer.id);
                      setOffers((prev: any) => prev.filter((o: any) => o.id !== expiredOffer.id));
                    }
                    closeExpired();
                  },
                },
                {
                  label: resubmitLoading ? 'Sending...' : 'Request Resubmit',
                  variant: 'primary',
                  disabled: resubmitLoading,
                  onClick: async () => {
                    const off = expiredOffer;
                    if (!off?.id) return;
                    setResubmitError('');
                    setResubmitLoading(true);
                    try {
                      await Api.requestResubmitOffer({ offerId: off.id as number });
                      setOffers((prev: any) => prev.filter((o: any) => o.id !== off.id));
                      setExpiredOffer(null);
                      setActionMessage(`Resubmit request sent — watch your email for a new offer if ${off?.hygienist?.firstName || 'the talent'} is still available.`);
                    } catch (e) {
                      setResubmitError('Something went wrong sending the request. Please try again.');
                    } finally {
                      setResubmitLoading(false);
                    }
                  },
                },
              ];
          return (
            <BrandedAlertModal
              isOpen={!!expiredOffer}
              onClose={closeExpired}
              headerLabel="Custom Offer"
              headerIcon={<OfferClockIcon />}
              illustration={<OfferClockIcon />}
              illustrationVariant="danger"
              title={alreadyRequested ? 'Resubmit Already Requested' : 'This Offer Has Expired'}
              ariaLabel="Offer expired"
              actions={actions}
            >
              {alreadyRequested ? (
                <>This offer expired and you've already asked {fname} to resubmit. Watch your email for a new offer if they're still available.</>
              ) : (
                <>
                  {fname}'s offer for this shift timed out before it was accepted. You can ask {fname} to resubmit their offer — they'll get a notification and email and can resubmit if they're still available.
                  {resubmitError && <div style={{ color: '#FB7672', marginTop: 8 }}>{resubmitError}</div>}
                </>
              )}
            </BrandedAlertModal>
          );
        })()}

        {/* 123D Boost Restriction */}
        <CorporateRestrictionModal
          isOpen={is123DBoostModalOpen}
          onClose={() => setIs123DBoostModalOpen(false)}
        />

        {/* Payment gating modal */}
        <PaymentRequiredModal
          isOpen={isPaymentModalOpen}
          locationId={paymentLocationId}
          requiresCardSetup={requiresCardSetup}
          isCardInvalid={isCardInvalid}
          checkpoint={paymentCheckpoint}
          sourcePage="job_posted"
          onGoPaymentInfo={handleGoPaymentInfo}
          onClose={() => setIsPaymentModalOpen(false)}
        />
      </BasicContainer>
    </>
  )
}
